import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
    FiSearch,
    FiX,
    FiPlus,
    FiMinus,
    FiMail,
    FiArrowRight,
    FiHelpCircle,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientAdSense from "../components/Client/ClientAdSense";

const CATEGORIES = ["All", "Getting Started", "Usage", "Pricing", "Privacy", "About"];

const FAQS = [
    {
        category: "Getting Started",
        q: "What is PicPromptify?",
        a: "PicPromptify is a free online tool that generates creative text prompts for AI image generators like Midjourney, DALL·E, Stable Diffusion, and more. You don't generate images on our site — instead, we give you high-quality prompts you can copy and use elsewhere.",
    },
    {
        category: "Getting Started",
        q: "Does PicPromptify generate or edit images?",
        a: "No. PicPromptify does not create, edit, store, or display any images. We only provide text-based prompts (e.g., \"cyberpunk cat wearing neon goggles, digital art, 4k\") that you can paste into your favorite AI image generator.",
    },
    {
        category: "Usage",
        q: "How do I use the prompts?",
        a: "1) Visit picpromptify.com. 2) Get a fresh AI image prompt (automatically shown or via refresh). 3) Copy the prompt. 4) Paste it into Midjourney, DALL·E, Bing Image Creator, or any compatible AI tool. 5) Generate your image there.",
    },
    {
        category: "Pricing",
        q: "Is PicPromptify free?",
        a: "Yes — our AI prompt generator is 100% free, requires no sign-up, and works instantly. We support the site through Google AdSense ads — no hidden costs.",
    },
    {
        category: "Privacy",
        q: "Do you store my data or prompts?",
        a: "We do not collect or store personal data from prompt usage. We use Firebase and Google Analytics to understand general traffic (e.g., page views, country, device type) — not your prompts or identity. All data is anonymized. For full details, see our Privacy Policy.",
        link: { href: "/privacy-policy", label: "Privacy Policy" },
    },
    {
        category: "Usage",
        q: "Can I use these prompts commercially?",
        a: "Yes! Prompts generated by PicPromptify are free to use for personal and commercial projects. Since we only provide text descriptions (not images), you retain full rights to any images you create using them — subject to the terms of the AI platform you use (e.g., Midjourney's content policy).",
    },
    {
        category: "Pricing",
        q: "Why are there ads on the site?",
        a: "PicPromptify is free thanks to Google AdSense. Ads help cover domain and hosting costs (via Firebase). We keep them non-intrusive — thank you for supporting us!",
    },
    {
        category: "Usage",
        q: "Does it work on mobile?",
        a: "Yes! PicPromptify is fully mobile-friendly. Get prompt ideas on your phone, tablet, or desktop — no app needed.",
    },
    {
        category: "About",
        q: "Who created PicPromptify?",
        a: "PicPromptify is an independent project built to help creators, artists, and designers find inspiration faster. It is not affiliated with OpenAI, Midjourney, Stability AI, or Google.",
    },
    {
        category: "About",
        q: "How can I contact you?",
        a: "Found a bug? Have a prompt idea? Want to suggest a category? Email us at picpromptify@gmail.com.",
        link: { href: "mailto:picpromptify@gmail.com", label: "Email us" },
    },
];

const FAQItem = ({ item, isOpen, onToggle, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
        className='ios-glass-dark rounded-2xl overflow-hidden'
        style={{
            border: isOpen
                ? "1px solid rgba(127,0,255,0.4)"
                : "1px solid rgba(255,255,255,0.08)",
            boxShadow: isOpen
                ? "0 14px 36px -12px rgba(127,0,255,0.4), inset 0 1px 0 rgba(255,255,255,0.12)"
                : "0 8px 22px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)",
            transition: "border 0.3s, box-shadow 0.3s",
        }}
    >
        <button
            onClick={onToggle}
            className='w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left group'
        >
            <div className='flex items-start gap-3 min-w-0 flex-1'>
                <div
                    className='w-7 h-7 md:w-8 md:h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5'
                    style={{
                        background: isOpen
                            ? "linear-gradient(135deg, #7F00FF, #FF8AFF)"
                            : "rgba(255,255,255,0.06)",
                        border: isOpen
                            ? "1px solid rgba(255,255,255,0.2)"
                            : "1px solid rgba(255,255,255,0.1)",
                        boxShadow: isOpen
                            ? "0 6px 16px -4px rgba(127,0,255,0.55)"
                            : "none",
                    }}
                >
                    <FiHelpCircle
                        className='text-sm md:text-base'
                        style={{ color: isOpen ? "#fff" : "rgba(255,255,255,0.55)" }}
                    />
                </div>
                <h3
                    className={`text-sm md:text-base font-semibold tracking-tight transition-colors leading-snug pt-1 ${
                        isOpen
                            ? "text-client-title"
                            : "text-client-font group-hover:text-client-title"
                    }`}
                >
                    {item.q}
                </h3>
            </div>
            <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ type: "spring", stiffness: 320, damping: 18 }}
                className='flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center'
                style={{
                    background: isOpen
                        ? "linear-gradient(135deg, #FCFA02, #FF8AFF)"
                        : "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                }}
            >
                {isOpen ? (
                    <FiMinus className='text-sm text-client-back' />
                ) : (
                    <FiPlus className='text-sm text-client-font/85' />
                )}
            </motion.div>
        </button>
        <AnimatePresence initial={false}>
            {isOpen && (
                <motion.div
                    key='content'
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    style={{ overflow: "hidden" }}
                >
                    <div className='px-4 md:px-5 pb-5 pt-1 ml-10 md:ml-11'>
                        <div
                            className='border-l-2 pl-4 text-client-font/75 text-sm md:text-[15px] leading-relaxed'
                            style={{ borderColor: "rgba(127,0,255,0.5)" }}
                        >
                            {item.a}
                            {item.link && (
                                <div className='mt-3'>
                                    <a
                                        href={item.link.href}
                                        target={
                                            item.link.href.startsWith("http")
                                                ? "_blank"
                                                : undefined
                                        }
                                        rel={
                                            item.link.href.startsWith("http")
                                                ? "noopener noreferrer"
                                                : undefined
                                        }
                                        className='inline-flex items-center gap-1 font-semibold text-xs md:text-sm underline decoration-2 underline-offset-2'
                                        style={{ color: "#FCFA02" }}
                                    >
                                        {item.link.label} <FiArrowRight />
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </motion.div>
);

const Faq = () => {
    const [query, setQuery] = useState("");
    const [activeCat, setActiveCat] = useState("All");
    const [openIdx, setOpenIdx] = useState(0);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return FAQS.filter((f) => {
            const inCat = activeCat === "All" || f.category === activeCat;
            const inQuery =
                !q ||
                f.q.toLowerCase().includes(q) ||
                f.a.toLowerCase().includes(q);
            return inCat && inQuery;
        });
    }, [query, activeCat]);

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* HERO */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 40, -20, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />
                <div className='relative mx-auto max-w-4xl px-4 sm:px-6 md:px-8 pt-8 md:pt-14 pb-6 md:pb-8 text-center'>
                    {/* DSPY1 — top-of-page display ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10 text-left'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>Help Center</span>
                        </div>
                        <h1 className='text-4xl md:text-6xl font-bold text-client-font tracking-tight leading-tight'>
                            Frequently asked{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                questions
                            </span>
                        </h1>
                        <p className='text-client-font/60 text-sm md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed'>
                            Quick answers about PicPromptify. Can't find what you're looking
                            for? Email us anytime.
                        </p>
                    </motion.div>

                    {/* Search */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className='mt-6 md:mt-8 max-w-xl mx-auto'
                    >
                        <div
                            className='flex items-center gap-2 md:gap-3 rounded-2xl px-3 md:px-4 py-2 md:py-2.5'
                            style={{
                                background: "rgba(255,255,255,0.06)",
                                backdropFilter: "blur(22px) saturate(180%)",
                                WebkitBackdropFilter: "blur(22px) saturate(180%)",
                                border: "1px solid rgba(255,255,255,0.14)",
                                boxShadow:
                                    "0 10px 28px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)",
                            }}
                        >
                            <FiSearch
                                className='text-lg md:text-xl flex-shrink-0'
                                style={{ color: "rgba(255,255,255,0.6)" }}
                            />
                            <input
                                type='text'
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder='Search FAQs…'
                                className='flex-1 bg-transparent outline-none border-0 text-client-font placeholder:text-client-font/40 text-sm md:text-base py-2'
                            />
                            <AnimatePresence>
                                {query && (
                                    <motion.button
                                        initial={{ opacity: 0, scale: 0.7 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.7 }}
                                        whileTap={{ scale: 0.9 }}
                                        whileHover={{ rotate: 90 }}
                                        onClick={() => setQuery("")}
                                        aria-label='Clear'
                                        className='w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center'
                                        style={{
                                            background: "rgba(255,255,255,0.08)",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            color: "rgba(255,255,255,0.75)",
                                        }}
                                    >
                                        <FiX className='text-sm' />
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>

                    {/* Category chips */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.25 }}
                        className='mt-5 md:mt-6 flex flex-wrap justify-center gap-2'
                    >
                        {CATEGORIES.map((c) => {
                            const active = activeCat === c;
                            return (
                                <motion.button
                                    key={c}
                                    whileHover={{ y: -2, scale: 1.04 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => setActiveCat(c)}
                                    className='px-3.5 py-1.5 rounded-full text-[11px] md:text-xs font-semibold transition-colors'
                                    style={{
                                        background: active
                                            ? "linear-gradient(135deg, #7F00FF, #FF8AFF)"
                                            : "rgba(255,255,255,0.05)",
                                        border: active
                                            ? "1px solid rgba(255,255,255,0.22)"
                                            : "1px solid rgba(255,255,255,0.12)",
                                        color: active
                                            ? "#fff"
                                            : "rgba(255,255,255,0.7)",
                                        boxShadow: active
                                            ? "0 8px 20px -6px rgba(127,0,255,0.5)"
                                            : "none",
                                    }}
                                >
                                    {c}
                                </motion.button>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* FAQ LIST */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-3xl space-y-3 md:space-y-4 mt-6 md:mt-10'>
                    {filtered.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex flex-col items-center justify-center text-center py-16'
                        >
                            <div
                                className='w-16 h-16 rounded-2xl flex items-center justify-center mb-4'
                                style={{
                                    background:
                                        "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                }}
                            >
                                <FiSearch className='text-2xl text-client-title' />
                            </div>
                            <h3 className='text-client-font font-bold text-lg tracking-tight'>
                                No matches found
                            </h3>
                            <p className='text-client-font/55 text-sm mt-2 max-w-md'>
                                Try a different keyword or browse all categories.
                            </p>
                        </motion.div>
                    ) : (
                        filtered.map((f, idx) => (
                            <FAQItem
                                key={f.q}
                                item={f}
                                index={idx}
                                isOpen={openIdx === idx}
                                onToggle={() => setOpenIdx(openIdx === idx ? -1 : idx)}
                            />
                        ))
                    )}

                    {/* Contact CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className='relative rounded-3xl p-6 md:p-8 overflow-hidden text-center mt-6 md:mt-10'
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(127,0,255,0.2), rgba(252,250,2,0.06))",
                            border: "1px solid rgba(127,0,255,0.35)",
                            backdropFilter: "blur(18px) saturate(180%)",
                            WebkitBackdropFilter: "blur(18px) saturate(180%)",
                            boxShadow:
                                "0 18px 40px -12px rgba(127,0,255,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
                        }}
                    >
                        <motion.div
                            animate={{ rotate: [0, 8, -8, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className='inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-3'
                            style={{
                                background:
                                    "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                boxShadow:
                                    "0 12px 28px -8px rgba(127,0,255,0.55)",
                            }}
                        >
                            <FiMail className='text-2xl text-white' />
                        </motion.div>
                        <h3 className='text-client-font font-bold text-xl md:text-2xl tracking-tight'>
                            Still have questions?
                        </h3>
                        <p className='text-client-font/65 text-sm md:text-base mt-2 mb-5 max-w-md mx-auto'>
                            We'd love to help. Drop us an email and we'll get back to you
                            soon.
                        </p>
                        <motion.a
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            href='mailto:picpromptify@gmail.com'
                            className='inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm'
                            style={{
                                background:
                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                boxShadow:
                                    "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                            }}
                        >
                            <FiMail />
                            Email us
                        </motion.a>
                    </motion.div>
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default Faq;
